import React, { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { HELP_DOCS, DEFAULT_SECTION_ID, HelpSection } from '../components/HelpDocs';

const groupOrder: HelpSection['group'][] = [
  '快速开始',
  '集成工作流',
  '核心概念',
  '常见问题',
  '参考',
];

const groupIcons: Record<HelpSection['group'], string> = {
  '快速开始': 'rocket_launch',
  '集成工作流': 'route',
  '核心概念': 'school',
  '常见问题': 'help',
  '参考': 'menu_book',
};

const HelpPage: React.FC = () => {
  // 通过 URL hash 同步当前 section（便于分享）
  const [activeId, setActiveId] = useState<string>(() => {
    const h = window.location.hash.replace(/^#/, '');
    return HELP_DOCS.find(s => s.id === h)?.id ?? DEFAULT_SECTION_ID;
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    const onHash = () => {
      const h = window.location.hash.replace(/^#/, '');
      const found = HELP_DOCS.find(s => s.id === h);
      if (found) setActiveId(found.id);
    };
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // 切换 section 时更新 hash 并滚到顶
  const goto = (id: string) => {
    setActiveId(id);
    window.location.hash = id;
    document.getElementById('help-content')?.scrollTo({ top: 0, behavior: 'auto' });
  };

  const grouped = useMemo(() => {
    const m = new Map<HelpSection['group'], HelpSection[]>();
    for (const s of HELP_DOCS) {
      if (!m.has(s.group)) m.set(s.group, []);
      m.get(s.group)!.push(s);
    }
    return m;
  }, []);

  const matches = (s: HelpSection) => {
    if (!search.trim()) return true;
    const k = search.toLowerCase();
    return s.title.toLowerCase().includes(k) || s.content.toLowerCase().includes(k);
  };

  const active = HELP_DOCS.find(s => s.id === activeId) ?? HELP_DOCS[0];

  return (
    <div className="min-h-full bg-surface flex">
      {/* 左侧目录 */}
      <aside className="w-72 flex-shrink-0 bg-surface-container-low border-r border-outline-variant overflow-y-auto" style={{ maxHeight: '100vh', position: 'sticky', top: 0 }}>
        <div className="p-5 border-b border-outline-variant">
          <h1 className="text-lg font-bold text-on-surface font-headline">帮助中心</h1>
          <p className="text-xs text-on-surface-variant mt-1 font-body">操作指南 · 工作流 · 常见问题</p>
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="搜索..."
            className="mt-3 w-full h-9 px-3 bg-white rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-200 border border-outline-variant"
          />
        </div>

        <nav className="p-3 space-y-4">
          {groupOrder.map(group => {
            const items = (grouped.get(group) ?? []).filter(matches);
            if (!items.length) return null;
            return (
              <div key={group}>
                <div className="text-[11px] font-semibold text-on-surface-variant uppercase tracking-wider px-3 mb-2 font-label flex items-center gap-1.5">
                  <span className="material-symbols-outlined" style={{ fontSize: '14px' }}>{groupIcons[group]}</span>
                  {group}
                </div>
                <ul className="space-y-0.5">
                  {items.map(s => (
                    <li key={s.id}>
                      <button
                        onClick={() => goto(s.id)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-colors font-body ${
                          activeId === s.id
                            ? 'bg-primary-50 text-primary-700 font-medium'
                            : 'text-on-surface-variant hover:bg-white hover:text-on-surface'
                        }`}
                      >
                        {s.title}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </nav>
      </aside>

      {/* 右侧内容 */}
      <main id="help-content" className="flex-1 overflow-y-auto" style={{ maxHeight: '100vh' }}>
        <article className="max-w-3xl mx-auto px-8 py-10 prose prose-slate prose-headings:font-headline prose-h1:text-3xl prose-h1:mb-6 prose-h2:text-xl prose-h2:mt-10 prose-h2:pb-2 prose-h2:border-b prose-h2:border-outline-variant prose-p:font-body prose-p:leading-relaxed prose-li:font-body prose-table:text-sm prose-table:my-4 prose-th:bg-surface-container-low prose-th:px-3 prose-th:py-2 prose-td:px-3 prose-td:py-2 prose-td:border-outline-variant prose-blockquote:border-l-primary prose-blockquote:bg-primary-50/30 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:not-italic prose-blockquote:text-on-surface prose-code:bg-surface-container-high prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-primary-700 prose-code:before:content-none prose-code:after:content-none prose-a:text-primary prose-a:no-underline hover:prose-a:underline">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{active.content}</ReactMarkdown>
        </article>
      </main>
    </div>
  );
};

export default HelpPage;
